import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FileDown, PlayCircle } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { EmptyState } from '@/client/components/ui/empty-state';
import { api } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';
import type { PortalEnrollmentRow } from '@/client/types/courses';

export function PortalCourses() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  const list = useQuery({
    queryKey: ['portal', 'enrollments'],
    queryFn: () => api<PortalEnrollmentRow[]>('/api/v1/me/enrollments'),
  });

  if (list.isLoading) {
    return (
      <div className="grid gap-5 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className="dr-card-static h-[18rem] animate-shimmer" />
        ))}
      </div>
    );
  }

  if (!list.data || list.data.length === 0) {
    return (
      <EmptyState
        variant="page"
        title={t('portal.empty_courses_title')}
        description={t('portal.empty_courses_body')}
        action={
          <Link to={`/${locale}/kurzy`}>
            <Button>
              {t('portal.empty_courses_cta')}
              <ArrowRight size={16} />
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 stagger-children">
      {list.data.map((row) => {
        const pct = Math.max(0, Math.min(100, row.progress.percent));
        return (
          <article
            key={row.enrollment.id}
            className="dr-card flex flex-col overflow-hidden group hover:-translate-y-1"
          >
            <Link
              to={`/${locale}/portal/kurzy/${row.course.slug}`}
              className="relative aspect-[16/9] overflow-hidden block"
            >
              {row.course.thumbnailUrl ? (
                <img
                  src={row.course.thumbnailUrl}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div
                  className="h-full w-full"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(193,167,226,0.55) 0%, rgba(117,59,189,0.35) 100%)',
                  }}
                />
              )}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, transparent 45%, rgba(45,21,72,0.55) 100%)',
                }}
                aria-hidden
              />
              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-pill bg-white/80 backdrop-blur-md border border-glass-border-strong text-primary-dark px-2.5 py-1 font-sans text-caption font-medium">
                {pct}%
              </span>
              <span className="absolute bottom-3 right-3 inline-flex items-center justify-center h-10 w-10 rounded-pill bg-white/85 backdrop-blur-md text-primary shadow-glass-soft group-hover:scale-110 transition-transform">
                <PlayCircle size={20} />
              </span>
            </Link>

            <div className="flex flex-1 flex-col p-5 sm:p-6">
              <Link to={`/${locale}/portal/kurzy/${row.course.slug}`}>
                <h3 className="font-display text-h4 text-primary-dark group-hover:text-primary transition-colors">
                  {row.course.title}
                </h3>
              </Link>
              <p className="mt-2 font-sans text-body-small text-text-secondary">
                {t('courses.portal_progress', {
                  completed: row.progress.completedEpisodes,
                  total: row.progress.totalEpisodes,
                })}
              </p>

              <div className="mt-4 h-2 rounded-pill bg-glass-border overflow-hidden">
                <div
                  className="h-full rounded-pill transition-all duration-700"
                  style={{
                    width: `${pct}%`,
                    background: 'linear-gradient(90deg, #c1a7e2 0%, #753bbd 50%, #512d6d 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                />
              </div>

              <div className="mt-auto pt-5 flex items-center justify-between gap-3">
                <Link
                  to={`/${locale}/portal/kurzy/${row.course.slug}`}
                  className="inline-flex items-center gap-1.5 font-sans text-body-small font-medium text-primary group-hover:gap-2.5 transition-all"
                >
                  {t('courses.continue_cta')}
                  <ArrowRight size={14} />
                </Link>
                {row.invoice?.hasPdf && (
                  <a
                    href={`/api/v1/me/enrollments/${row.enrollment.id}/invoice`}
                    className="inline-flex items-center gap-1.5 rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong text-text-secondary hover:text-primary hover:border-primary/30 px-3 h-8 font-sans text-caption font-medium transition-all"
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileDown size={12} />
                    {t('portal_extras.invoice_link')}
                    {row.invoice.number ? ` ${row.invoice.number}` : ''}
                  </a>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
